import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ModernLogo } from "@/components/ModernLogo";
import { SEO } from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useMembership } from "@/hooks/useMembership";
import { MEMBERSHIP, whatsappLink } from "@/config/membership";
import { ContractorDirectory } from "@/components/membership/ContractorDirectory";
import { QuoteCheckSection } from "@/components/membership/QuoteCheckSection";
import { ArrowLeft, MessageCircle } from "lucide-react";

interface Guide {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  content: string;
  category: string | null;
  publish_month: string | null;
}

export default function MembershipHub() {
  const { membership, isMember, loading } = useMembership();
  const [guides, setGuides] = useState<Guide[]>([]);
  const [openGuide, setOpenGuide] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!isMember) return;
    const load = async () => {
      const { data, error } = await supabase
        .from("membership_guides")
        .select("id, slug, title, summary, content, category, publish_month")
        .eq("is_published", true)
        .order("publish_month", { ascending: false });
      if (error) {
        console.error("Guides load failed:", error.message);
        return;
      }
      setGuides((data as Guide[]) ?? []);
    };
    load();
  }, [isMember]);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("membership-portal");
      if (error) throw error;
      if (!data?.url) throw new Error("No portal URL");
      window.location.href = data.url as string;
    } catch (error) {
      console.error("Portal failed:", error);
      toast({
        title: "Could not open billing",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading your membership…
      </div>
    );
  }

  if (!isMember) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="max-w-md w-full border-border/50">
          <CardHeader>
            <CardTitle>Members only</CardTitle>
            <CardDescription>
              Join the Trades &amp; Property Maintenance Membership for {MEMBERSHIP.priceLabel}/month
              to unlock guides, the contractor directory and quote checking.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate("/membership")}>
              See membership details
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO
        title="Member Area | Sortable Membership"
        description="Your Sortable membership hub: monthly maintenance guides, WhatsApp advice, quote checking and the trusted contractor directory."
        keywords="sortable member area, property maintenance guides, quote checking, contractor directory"
        canonical="https://sortable.uk/membership/hub"
        noIndex
      />

      <header className="bg-background/95 backdrop-blur-sm border-b border-border/50 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-2 md:py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <ModernLogo showTagline={false} />
          </Link>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">Member</Badge>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Dashboard</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Your member area</h1>
            <p className="text-muted-foreground text-sm">
              {membership?.current_period_end
                ? `${membership.cancel_at_period_end ? "Ends" : "Renews"} on ${new Date(
                    membership.current_period_end,
                  ).toLocaleDateString("en-GB")}`
                : "Membership active"}
            </p>
          </div>
          <div className="flex gap-2">
            <a href={whatsappLink()} target="_blank" rel="noopener noreferrer">
              <Button variant="secondary">
                <MessageCircle className="h-4 w-4 mr-2" />
                WhatsApp advice
              </Button>
            </a>
            <Button variant="outline" onClick={openPortal} disabled={portalLoading}>
              {portalLoading ? "Opening…" : "Manage billing"}
            </Button>
          </div>
        </div>

        <Tabs defaultValue="guides">
          <TabsList className="w-full grid grid-cols-3">
            <TabsTrigger value="guides">Guides</TabsTrigger>
            <TabsTrigger value="quotes">Quote checks</TabsTrigger>
            <TabsTrigger value="directory">Directory</TabsTrigger>
          </TabsList>

          <TabsContent value="guides" className="mt-4 space-y-4">
            {guides.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Your first monthly guide will appear here shortly.
              </p>
            ) : (
              guides.map((guide) => (
                <Card key={guide.id} className="border-border/50">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-lg">{guide.title}</CardTitle>
                      {guide.category && <Badge variant="secondary">{guide.category}</Badge>}
                    </div>
                    {guide.summary && <CardDescription>{guide.summary}</CardDescription>}
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {openGuide === guide.id && (
                      <div className="whitespace-pre-wrap text-sm text-muted-foreground">
                        {guide.content}
                      </div>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setOpenGuide(openGuide === guide.id ? null : guide.id)}
                    >
                      {openGuide === guide.id ? "Close guide" : "Read guide"}
                    </Button>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="quotes" className="mt-4">
            <QuoteCheckSection />
          </TabsContent>

          <TabsContent value="directory" className="mt-4">
            <ContractorDirectory />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
